import React, { useEffect, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    Heading,
    HStack,
    Input,
    NativeSelect,
    SimpleGrid,
    Stack,
    Table,
} from '@chakra-ui/react';
import { EditIcon, PlusIcon, TrashIcon } from '../components/Icons';
import { api, Branch, Company } from '../api/api';
import { useConfirmation, useNotification } from '../context/NotificationContext';
import { FormControl, FormErrorMessage, FormLabel } from '../components/FormControls';
import { Panel, PanelBody, PanelHeader } from '../components/Panel';
import SimpleModal from '../components/SimpleModal';
import { useNavigate } from 'react-router-dom';

const BranchesPage: React.FC = () => {
    const [branches, setBranches] = useState<Branch[]>([]);
    const [companies, setCompanies] = useState<Company[]>([]);
    const [showForm, setShowForm] = useState(false);
    const [form, setForm] = useState<Partial<Branch>>({
        companyId: undefined,
        name: '',
        location: '',
        contact: '',
    });
    const [editId, setEditId] = useState<number | null>(null);
    const [validation, setValidation] = useState({ companyId: true, name: true });
    const [error, setError] = useState<string>('');
    const [branchCounts, setBranchCounts] = useState<Record<number, { active: number; deactivated: number }>>({});
    const { confirm } = useConfirmation();
    const { notify } = useNotification();
    const navigate = useNavigate();

    const loadData = () => {
        setError('');
        Promise.all([
            api.branches.list(),
            api.companies.list(),
            api.systems.list({ includeInactive: true })
        ])
            .then(([branchList, companyList, systems]) => {
                setBranches(branchList);
                setCompanies(companyList);

                const counts: Record<number, { active: number; deactivated: number }> = {};
                systems.forEach(system => {
                    const id = system.branchId;
                    if (!counts[id]) counts[id] = { active: 0, deactivated: 0 };
                    if (system.isActive === false) counts[id].deactivated += 1;
                    else counts[id].active += 1;
                });
                setBranchCounts(counts);
            })
            .catch(err => setError(err.message || 'Failed to load branches'));
    };

    useEffect(() => {
        loadData();
    }, []);

    const openCreate = () => {
        setShowForm(true);
        setEditId(null);
        setForm({ companyId: undefined, name: '', location: '', contact: '' });
        setValidation({ companyId: true, name: true });
    };

    const openEdit = (branch: Branch) => {
        setShowForm(true);
        setEditId(branch.id);
        setForm(branch);
        setValidation({ companyId: true, name: true });
    };

    const cancelForm = () => {
        setShowForm(false);
        setEditId(null);
        setForm({ companyId: undefined, name: '', location: '', contact: '' });
        setValidation({ companyId: true, name: true });
    };

    const validateForm = () => {
        const next = {
            companyId: !!form.companyId,
            name: !!form.name?.trim(),
        };
        setValidation(next);
        return next.companyId && next.name;
    };

    const handleSubmit = async () => {
        if (!validateForm()) return;
        try {
            if (editId == null) {
                await api.branches.create(form as Branch);
                notify({ type: 'success', message: 'Branch created successfully' });
            } else {
                await api.branches.update(form as Branch);
                notify({ type: 'success', message: 'Branch updated successfully' });
            }
            cancelForm();
            loadData();
        } catch (err: any) {
            const errorMessage = err?.response?.data?.error || err?.response?.data?.title || err?.message || 'Failed to save branch';
            notify({ type: 'error', message: errorMessage });
        }
    };

    const handleDelete = (branch: Branch) => {
        confirm({
            title: 'Delete Branch',
            message: `Delete ${branch.name}? This action cannot be undone.`,
            confirmText: 'Delete',
            cancelText: 'Cancel',
            confirmColorScheme: 'red',
            onConfirm: async () => {
                try {
                    await api.branches.delete(branch.id);
                    notify({ type: 'success', message: 'Branch deleted' });
                    loadData();
                } catch (err: any) {
                    const errorMessage = err?.response?.data?.error || err?.response?.data?.title || err?.message || 'Failed to delete branch';
                    notify({ type: 'error', message: errorMessage });
                }
            },
        });
    };

    return (
        <Stack gap={6}>
            <HStack justify="space-between" flexWrap="wrap" gap={3}>
                <Heading size="lg">Branches</Heading>
                <Button colorPalette="brand" onClick={openCreate} display="inline-flex" alignItems="center" gap={2}>
                    <PlusIcon />
                    New Branch
                </Button>
            </HStack>

            {error && (
                <Alert.Root status="error" borderRadius="md">
                    <Alert.Content>
                        <Alert.Description>{error}</Alert.Description>
                    </Alert.Content>
                </Alert.Root>
            )}

            <Panel>
                <PanelHeader>
                    <Heading size="md">Branch Directory</Heading>
                </PanelHeader>
                <PanelBody>
                    <Box overflow="auto" maxH="60vh">
                    <Table.Root>
                        <Table.Header position="sticky" top="0" bg="white" zIndex={1}>
                            <Table.Row>
                                <Table.ColumnHeader>Name</Table.ColumnHeader>
                                <Table.ColumnHeader>Company</Table.ColumnHeader>
                                <Table.ColumnHeader>Location</Table.ColumnHeader>
                                <Table.ColumnHeader>Contact</Table.ColumnHeader>
                                <Table.ColumnHeader textAlign="center">Active Machines</Table.ColumnHeader>
                                <Table.ColumnHeader textAlign="center">Deactivated Machines</Table.ColumnHeader>
                                <Table.ColumnHeader textAlign="right">Actions</Table.ColumnHeader>
                            </Table.Row>
                        </Table.Header>
                        <Table.Body>
                            {branches.map(branch => (
                                <Table.Row key={branch.id}>
                                    <Table.Cell>{branch.name}</Table.Cell>
                                    <Table.Cell>{branch.company?.name || '—'}</Table.Cell>
                                    <Table.Cell>{branch.location || '—'}</Table.Cell>
                                    <Table.Cell>{branch.contact || '—'}</Table.Cell>
                                    <Table.Cell textAlign="center">{branchCounts[branch.id]?.active ?? 0}</Table.Cell>
                                    <Table.Cell textAlign="center">{branchCounts[branch.id]?.deactivated ?? 0}</Table.Cell>
                                    <Table.Cell textAlign="right">
                                        <HStack gap={1} justify="flex-end">
                                            <Button
                                                size="sm"
                                                variant="outline"
                                                colorPalette="blue"
                                                onClick={() => navigate(`/systems?companyId=${branch.companyId}&branchId=${branch.id}`)}
                                                title="Machines"
                                            >
                                                Machines
                                            </Button>
                                            <Button size="sm" variant="outline" onClick={() => openEdit(branch)} title="Edit">
                                                <EditIcon />
                                            </Button>
                                            <Button
                                                size="sm"
                                                variant="outline"
                                                colorPalette="red"
                                                onClick={() => handleDelete(branch)}
                                                title="Delete"
                                            >
                                                <TrashIcon />
                                            </Button>
                                        </HStack>
                                    </Table.Cell>
                                </Table.Row>
                            ))}
                        </Table.Body>
                    </Table.Root>
                    </Box>
                </PanelBody>
            </Panel>

            {showForm && (
                <SimpleModal
                    isOpen={showForm}
                    onClose={cancelForm}
                    title={editId == null ? 'New Branch' : 'Edit Branch'}
                    size="md"
                    footer={
                        <HStack gap={3} justify="flex-end">
                            <Button variant="ghost" onClick={cancelForm}>
                                Cancel
                            </Button>
                            <Button colorPalette="brand" onClick={handleSubmit}>
                                {editId == null ? 'Create Branch' : 'Update Branch'}
                            </Button>
                        </HStack>
                    }
                >
                    <SimpleGrid columns={{ base: 1, md: 2 }} gap={4}>
                        <FormControl isRequired isInvalid={!validation.companyId}>
                            <FormLabel>Company</FormLabel>
                            <NativeSelect.Root>
                                <NativeSelect.Field
                                    placeholder="Select company"
                                    value={form.companyId || ''}
                                    onChange={e => setForm(f => ({ ...f, companyId: Number(e.target.value) }))}
                                >
                                    {companies.map(company => (
                                        <option key={company.id} value={company.id}>
                                            {company.name}
                                        </option>
                                    ))}
                                </NativeSelect.Field>
                                <NativeSelect.Indicator />
                            </NativeSelect.Root>
                            <FormErrorMessage>Company is required.</FormErrorMessage>
                        </FormControl>

                        <FormControl isRequired isInvalid={!validation.name}>
                            <FormLabel>Branch Name</FormLabel>
                            <Input
                                value={form.name || ''}
                                onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                            />
                            <FormErrorMessage>Branch name is required.</FormErrorMessage>
                        </FormControl>

                        <FormControl>
                            <FormLabel>Location</FormLabel>
                            <Input
                                value={form.location || ''}
                                onChange={e => setForm(f => ({ ...f, location: e.target.value }))}
                            />
                        </FormControl>

                        <FormControl>
                            <FormLabel>Contact</FormLabel>
                            <Input
                                value={form.contact || ''}
                                onChange={e => setForm(f => ({ ...f, contact: e.target.value }))}
                            />
                        </FormControl>
                    </SimpleGrid>
                </SimpleModal>
            )}
        </Stack>
    );
};

export default BranchesPage;
